import { z } from 'zod';
import type { EventPayloads, EventType } from './events';

/**
 * The runtime half of the event contract.
 *
 * `events.ts` is a compile-time map, and it used to say validation at the wire was deliberately not
 * added, because `decodeKafkaMessage` has to stay permissive: it accepts three envelope shapes
 * (Debezium's router, the retry path's, and the relay's) and tightening it would break the recovery
 * paths that exist to survive exactly that kind of mismatch.
 *
 * That reasoning is about the **envelope**, and it still holds — nothing here touches the decoder.
 * These schemas validate the **payload**, at the one place the code already names as the single
 * point where a decoded message is asserted to be the event its type says it is. By then the
 * envelope has been normalised and the event type has chosen the handler, so this is a check on the
 * thing the handler is about to index into.
 *
 * What it buys: a field renamed at a producer and not at a consumer is already a compile error, but
 * a *hand-written* envelope is not typechecked — the retry path rebuilds one, the relay rebuilds
 * one, and Debezium's field placement is configuration rather than code. Any of those can deliver a
 * payload missing the field a handler dereferences, and the failure surfaces half an hour later as
 * a run that died for an unrelated-looking reason.
 *
 * `satisfies` is what keeps these honest: a schema whose inferred type drifts from `EventPayloads`
 * fails to compile here, so the runtime shape and the compile-time one cannot disagree.
 */
const pollTick = z.object({
  runId: z.string(),
  jobId: z.string(),
  notBefore: z.string(),
  pollChainToken: z.string()
});

const mcapEntry = z.object({
  uri: z.string(),
  objectKey: z.string(),
  itemId: z.string(),
  fileId: z.string().nullable(),
  bytes: z.number(),
  sortOrder: z.number()
});

export const EVENT_SCHEMAS = {
  'upload.file.completed': z.object({
    folderId: z.string(),
    itemId: z.string(),
    fileId: z.string(),
    objectKey: z.string(),
    bytes: z.number(),
    declaredChecksum: z.string().nullable().optional(),
    source: z.literal('s3_api').optional()
  }),
  'upload.file.admitted': z.object({
    folderId: z.string(),
    itemId: z.string(),
    fileId: z.string(),
    objectGeneration: z.number(),
    outcome: z.enum(['ready', 'rejected', 'check_error']),
    reasonCode: z.string().nullable()
  }),
  'dataset.evaluation.admitted': z.object({
    folderId: z.string(),
    runId: z.string(),
    triggerSource: z.string()
  }),
  'dataset.mcap.extracted': z.object({
    runId: z.string(),
    folderId: z.string(),
    entries: z.array(mcapEntry),
    meta: z.array(z.object({ name: z.string(), uri: z.string() }))
  }),
  'qa.job.submitted': pollTick,
  'qa.poll.due': pollTick,
  'qa.job.status.changed': z.object({
    runId: z.string(),
    vendorStatus: z.string().optional(),
    rawBody: z.string().optional(),
    rePersist: z.boolean().optional()
  }),
  'qa.run.completed': z.object({
    runId: z.string(),
    folderId: z.string(),
    terminalStatus: z.enum(['completed', 'rejected', 'ingest_failed', 'failed', 'timed_out']),
    failureReason: z.string().nullable()
  }),
  'dataset.decision.recorded': z.object({
    folderId: z.string(),
    runId: z.string().nullable(),
    decision: z.enum(['approved', 'rejected']),
    decidedBy: z.string()
  }),
  'media.preview.requested': z.object({
    folderId: z.string(),
    episodeId: z.string(),
    videoKeys: z.array(z.string())
  })
} satisfies { [K in EventType]: z.ZodType<EventPayloads[K]> };

/**
 * Every payload may also carry `notBefore`: the retry path adds one to whatever it republishes, and
 * `WorkerService.defer` honours it generically for any topic (INV-38). Schemas are therefore
 * non-strict — an unknown property is tolerated, because additive fields are how event versions
 * evolve without a flag day, and rejecting them would make a rolling deploy impossible.
 *
 * What is *not* tolerated is a missing or wrong-typed field the handler needs.
 */
export function parseEventPayload<T extends EventType>(type: T, payload: unknown): EventPayloads[T] {
  return EVENT_SCHEMAS[type].parse(payload) as EventPayloads[T];
}

/** Non-throwing form, for the producer side where the caller wants to report rather than crash. */
export function safeParseEventPayload<T extends EventType>(type: T, payload: unknown) {
  return EVENT_SCHEMAS[type].safeParse(payload);
}

export const isKnownEventType = (type: string): type is EventType => type in EVENT_SCHEMAS;
